import { useModals } from 'almanac-ui/Modal'
import { MultiStepWizard } from '../MultiStepWizard/MultiStepWizard'
import { AddPhoto } from './AddPhoto/AddPhoto'
import { DesignWizardProps, Values } from './Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath, useHistory } from 'react-router-dom'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DesignChannel } from 'components/NewFileModal/Types'
import { DESIGN_PATH, FOLDER_PATH } from 'paths'
import { useCreateDesign } from './useCreateDesign'
import { useState } from 'react'
import { WizardProvider } from '../Wizard/WizardContext'
import { PurposeAndGoals } from './PurposeAndGoals/PurposeAndGoals'
import { TemplateAndCaption } from './TemplateAndCaption/TemplateAndCaption'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { MobilePurposeAndGoals } from './PurposeAndGoals/MobilePurposeAndGoals'
import { showInternalServerErrorMessage } from 'common/ToastMessage/utils'
import { ImagesProvider } from '../SelectImages/ImagesProvider'
import { VideoWizard } from './VideoWizard/VideoWizard'
import { isSimplifiedWizard } from './utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { TYPES_WITH_PREVIEW } from 'screens/Calendar/FullScreenPreview/Types'
import dayjs from 'dayjs'

const DesignWizard = (props: DesignWizardProps) => {
  const { closeAllModals, openModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const defaultFolder = useDefaultFolder()
  const { isMobile } = useResponsiveDesign()
  const { currentFolder } = useCurrentFolderStore()
  const { mutateAsync, isLoading } = useCreateDesign({ workspaceId })
  const history = useHistory()
  const isScheduledPostsV3 = useIsScheduledPostsV3()

  const [fieldsCompleted, setFieldsCompleted] = useState(false)
  const [showAddPhoto, setShowAddPhoto] = useState(true)
  const useSimplifiedWizards = useFlipperFlag('simplified_wizards')
  const isSimple = useSimplifiedWizards && isSimplifiedWizard(props.schema.meta.channel as DesignChannel)
  const shouldShowAddPhoto = !isSimple && showAddPhoto

  const schema = shouldShowAddPhoto
    ? props.schema
    : { ...props.schema, steps: [props.schema.steps[0], props.schema.steps[1]] }

  const createDesignMutation = async ({
    width,
    height,
    initial_media_file_id,
    initial_media_file_remove_background,
    filename,
    project,
    source_visual_element_id,
    brand_visual_kit_id,
    cross_post_accounts,
    topic,
    brand_voice_id,
    tone,
    language,
    caption,
    brand_kit_id,
    content,
  }: Values) => {
    const generatingToast = !shouldShowAddPhoto
      ? showToastMessage('Generating your post...', 'loading', undefined, 0)
      : undefined

    try {
      const seed = {
        apply_brand: false,
      }

      const firstAccount = cross_post_accounts?.find(
        (account) => account.provider === providerFromChannelType[props.schema.meta.channel as DesignChannel],
      )
      const otherAccounts = cross_post_accounts?.filter(
        (account) => account.external_integration_id !== firstAccount?.external_integration_id,
      )

      const projectId = project?.id || currentFolder?.id || defaultFolder?.id || ''

      const design = await mutateAsync({
        width,
        height,
        topic,
        brand_voice_id,
        tone,
        language,
        source_visual_element_id,
        title: filename,
        parent_id: projectId,
        channel: props.schema.meta.channel as DesignChannel,
        initial_media_file_id,
        initial_media_file_remove_background,
        external_integration_id: firstAccount?.external_integration_id,
        brand_visual_kit_id,
        captions: caption ? [caption] : undefined,
        selected_caption_id: caption ? 0 : undefined,
        cross_post_accounts: otherAccounts,
        seed,
        brand_kit_id,
        content,
        completed_actions: {
          template: Boolean(source_visual_element_id),
          generate_caption: Boolean(topic),
          core_image: Boolean(initial_media_file_id),
          apply_brand: seed.apply_brand,
        },
      })

      if (!design) {
        throw new Error('No almanac file found')
      }

      closeAllModals()
      const showPostPreview = design?.channel && TYPES_WITH_PREVIEW.includes(design.channel)

      if (props.onSuccess) {
        props.onSuccess({ almanac_file: design })
      } else {
        if (isScheduledPostsV3 && showPostPreview) {
          const { FullScreenPreviewWithCarousel } = await import(
            'screens/Calendar/FullScreenPreview/FullScreenPreviewWithCarousel'
          )
          openModal(FullScreenPreviewWithCarousel, {
            id: design.id,
            channel: design.channel as DesignChannel,
            showCarousel: false,
            filters: {},
            width: design.width,
            onClose: () => closeAllModals(),
            startDate: dayjs().toISOString(),
            height: design.height,
          })
        } else {
          if (otherAccounts?.length) {
            history.push(generatePath(FOLDER_PATH, { folderSlug: design.parent?.seo_string as string }))
          } else {
            history.push(generatePath(DESIGN_PATH, { key: design.id }))
          }
        }
      }
    } catch (error) {
      reportError(error)
      showInternalServerErrorMessage()
    } finally {
      generatingToast?.()
    }
  }

  const handleSubmit = async (values: Values) => {
    return createDesignMutation(values)
  }

  if (schema.meta.isVideo) {
    return (
      <VideoWizard
        {...props}
        onSubmit={handleSubmit}
        createDesign={createDesignMutation}
        initialValues={{ project: currentFolder || defaultFolder, ...props.initialValues }}
      />
    )
  }

  return (
    <WizardProvider schema={schema}>
      <ImagesProvider>
        <MultiStepWizard.Root
          {...props}
          schema={schema}
          onSubmit={handleSubmit}
          initialValues={{ project: currentFolder || defaultFolder, ...props.initialValues }}
        >
          {isMobile ? (
            <MobilePurposeAndGoals setFieldsCompleted={setFieldsCompleted} />
          ) : (
            <PurposeAndGoals setFieldsCompleted={setFieldsCompleted} />
          )}
          <TemplateAndCaption
            setFieldsCompleted={setFieldsCompleted}
            fieldsCompleted={fieldsCompleted}
            onTemplateChange={(hasPlaceholderImage) => setShowAddPhoto(hasPlaceholderImage)}
          />
          {shouldShowAddPhoto && <AddPhoto isLoading={isLoading} />}
        </MultiStepWizard.Root>
      </ImagesProvider>
    </WizardProvider>
  )
}

export { DesignWizard }
